import { RotateCcw, SearchX, TriangleAlert } from 'lucide-react'
import { HttpStatus } from '@/shared/constants/http-status'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/shared/ui/empty'
import { extractApiError, getErrorMessage } from '@/shared/utils/handle-api-error'

type QueryErrorStateProps = {
  /** El `error` que devuelve la query. Se acepta `unknown` porque es lo que entrega React Query. */
  error: unknown
  /**
   * Qué se estaba cargando, con artículo y en singular: «el expediente», «el inmueble».
   * Es lo ÚNICO que este componente sabe del dominio, y se lo inyecta quien lo usa: así el
   * primitivo sirve igual en clientes, inventario o lo que venga.
   */
  resource: string
  /**
   * Qué hacer al reintentar — normalmente `refetch`. Se ofrece SÓLO cuando el fallo puede
   * cambiar de resultado: ante un 404 el botón no aparece aunque lo pases.
   */
  onRetry?: () => void
  className?: string
} & Omit<React.ComponentProps<'div'>, 'children'>

/**
 * Lo que se muestra cuando una query que pinta una pantalla FALLA.
 *
 * **Por qué existe.** El `QueryClient` de la app no declara `throwOnError` para las queries
 * (`app/app.tsx`), así que un error no sube a ningún ErrorBoundary: muere dentro del componente.
 * El que no lo dibuja, no lo muestra. Y la forma más común de no dibujarlo era escribir
 * `isLoading || !data ? <Skeleton/>`, que ante un error deja `isLoading` en false y `data` en
 * `undefined` — o sea, el skeleton para siempre. Un componente que no distingue «cargando» de
 * «falló» miente, y encima miente con una animación que promete que algo está pasando.
 *
 * **Por qué el 404 va aparte.** «No está» y «no pude preguntar» son dos hechos distintos y piden
 * dos salidas distintas. Reintentar sirve para el segundo; ofrecerlo sobre un 404 es invitar a
 * golpear una puerta que ya contestó. Se decide por `statusCode` y no por el texto del servidor:
 * el mensaje es traducción, el código es contrato.
 */
export function QueryErrorState({
  error,
  resource,
  onRetry,
  className,
  ...props
}: QueryErrorStateProps) {
  const isNotFound = extractApiError(error)?.statusCode === HttpStatus.NOT_FOUND

  return (
    <Empty className={cn('border-none', className)} {...props}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{isNotFound ? <SearchX /> : <TriangleAlert />}</EmptyMedia>
        <EmptyTitle>
          {isNotFound ? `No encontramos ${resource}` : `No se pudo cargar ${resource}`}
        </EmptyTitle>
        <EmptyDescription>
          {isNotFound
            ? 'Puede que lo hayan eliminado o que el enlace ya no sea válido.'
            : getErrorMessage(error)}
        </EmptyDescription>
      </EmptyHeader>
      {onRetry && !isNotFound ? (
        <EmptyContent>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RotateCcw />
            Reintentar
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  )
}
